import React from 'react';

import { Tooltip } from '@components';
import useIsTruncated from '@hooks/useIsTruncated';
import formatCurrency from '@utils/numberFormater';

import {
  LeadStatusSummaryRow,
  LeadSummaryTab
} from '../../types/leadAnalytics';

interface LeadStatusSummaryTableProps {
  rows: LeadStatusSummaryRow[];
  statusList: { id: string; name: string; color: string }[];
  className?: string;
  appliedTab: LeadSummaryTab;
}

const NAME_COL = 'w-[140px] min-w-[140px]';
const VALUE_COL = 'w-[120px] min-w-[120px] grow';
const PINNED_SHADOW = 'shadow-pinnedColumnShadow';

const StatusHeaderCell: React.FC<{
  status: { id: string; name: string; color: string };
}> = ({ status }) => {
  const { ref: textRef, isTruncated } = useIsTruncated<HTMLSpanElement>(
    status.name
  );

  return (
    <div
      className={`flex ${VALUE_COL} shrink-0 items-start gap-1.5 p-3`}
      data-tooltip-id={`status-header-${status.id}`}
    >
      <span className="mt-0.5 flex h-4 shrink-0 items-center">
        <span
          className="size-2.5 rounded-sm"
          style={{ background: status.color }}
        />
      </span>
      <span
        ref={textRef}
        className="line-clamp-2 break-words text-xs font-medium text-cofeeNeutral8"
      >
        {status.name}
      </span>
      {isTruncated && (
        <Tooltip
          id={`status-header-${status.id}`}
          className="rounded-lg !bg-cofeeNeutral10"
        >
          <div className="max-w-xs whitespace-normal break-words px-2 py-1 text-sm text-cofeeNeutral1">
            {status.name}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

const AgentNameCell: React.FC<{ id: string; name: string }> = ({
  id,
  name
}) => {
  const { ref: textRef, isTruncated } = useIsTruncated<HTMLSpanElement>(name);

  return (
    <div
      className={`sticky left-0 z-10 ${NAME_COL} shrink-0 bg-white`}
      data-tooltip-id={`agent-name-${id}`}
    >
      <div
        className={`flex h-full items-center rounded-l-lg border border-cofeeNeutral5 bg-white p-3 ${PINNED_SHADOW}`}
      >
        <span
          ref={textRef}
          className="truncate text-sm font-medium text-content"
        >
          {name}
        </span>
      </div>
      {isTruncated && (
        <Tooltip
          id={`agent-name-${id}`}
          className="rounded-lg !bg-cofeeNeutral10"
        >
          <div className="max-w-xs whitespace-normal break-words px-2 py-1 text-sm text-cofeeNeutral1">
            {name}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

const LeadStatusSummaryTable: React.FC<LeadStatusSummaryTableProps> = ({
  rows,
  statusList,
  className = '',
  appliedTab
}) => {
  const getValues = (row: LeadStatusSummaryRow, status: string) => {
    if (appliedTab === LeadSummaryTab.COUNT) {
      return row.count[status] ?? 0;
    }
    return formatCurrency((row.revenue[status] ?? 0) / 100, 'INR');
  };

  const getTotalValue = (row: LeadStatusSummaryRow) => {
    if (appliedTab === LeadSummaryTab.COUNT) {
      return row.totalCount;
    }
    return formatCurrency((row.totalRevenue ?? 0) / 100, 'INR');
  };

  const renderValue = (value: string | number) => (
    <span className="text-sm text-content">
      {appliedTab === LeadSummaryTab.REVENUE && (
        <span className="mr-0.5 !font-inter">₹</span>
      )}
      {value}
    </span>
  );

  const totalLabel =
    appliedTab === LeadSummaryTab.COUNT ? 'Total Leads' : 'Total Revenue';

  return (
    <div className={`table-scroll w-full overflow-x-auto ${className}`}>
      <div className="inline-flex min-w-full flex-col gap-2">
        <div className="flex min-w-full items-stretch rounded-lg bg-cofeeNeutralBg">
          <div className={`sticky left-0 z-10 ${NAME_COL} shrink-0 bg-white`}>
            <div
              className={`flex h-full items-start rounded-l-lg border-r border-cofeeNeutral5 bg-cofeeNeutralBg p-3 ${PINNED_SHADOW}`}
            >
              <span className="text-xs font-medium text-cofeeNeutral8">
                Agent name
              </span>
            </div>
          </div>
          {statusList.map((status) => (
            <StatusHeaderCell key={status.id} status={status} />
          ))}
          <div className={`flex ${VALUE_COL} shrink-0 items-start p-3`}>
            <span className="text-xs font-medium text-cofeeNeutral8">
              {totalLabel}
            </span>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="flex min-h-30 min-w-full items-center justify-center rounded-lg border border-cofeeNeutral5 bg-white p-3 text-sm text-cofeeNeutral8">
            No data found
          </div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="flex min-w-full items-stretch">
              <AgentNameCell id={row.id} name={row.label} />
              <div className="flex flex-1 items-stretch rounded-r-lg border-y border-r border-cofeeNeutral5 bg-white">
                {statusList.map((status) => (
                  <div
                    key={`${row.id}-${status.id}`}
                    className={`flex ${VALUE_COL} shrink-0 items-center p-3`}
                  >
                    {renderValue(getValues(row, status.name))}
                  </div>
                ))}
                <div className={`flex ${VALUE_COL} shrink-0 items-center p-3`}>
                  {renderValue(getTotalValue(row))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LeadStatusSummaryTable;
